import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { APIResponse } from '../../../services/interface'
import { ApiGet, ApiPost } from '../../../services/Service'
import { StatusEnum } from '../../type'

export type CheckStatus = 'PASS' | 'FAIL' | 'DEGRADED' | 'SKIPPED'
export type RunStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'TIMED_OUT'
export type OverallStatus = 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY' | 'UNKNOWN'

export interface IHealthSummary {
  total: number
  passed: number
  failed: number
  degraded: number
  skipped: number
  criticalFailures: number
}

export interface IHealthResult {
  testId: string
  name: string
  category: string
  provider: string
  priority: 'P0' | 'P1' | 'P2'
  kind: 'health' | 'functional' | 'mock'
  sideEffect: string
  status: CheckStatus
  failureClass: string | null
  providerErrorCode: string | null
  message: string
  details: Record<string, unknown> | null
  remediation: string | null
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  correlationId: string
}

export interface IHealthRunSummary {
  runId: string
  /** Present only while RUNNING: live counter from the replica executing the run. */
  progress?: { completed: number; total: number; currentCheckId: string | null } | null
  status: RunStatus
  environment: string
  correlationId: string
  triggeredBy: string
  triggerSource: string
  requestedGroups: string[]
  allowSideEffects: boolean
  summary: IHealthSummary
  overall: OverallStatus
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  error: string | null
  createdAt: string
}

export interface IHealthRun extends IHealthRunSummary {
  results: IHealthResult[]
  criticalFailures: Array<{ testId: string; name: string; message: string; remediation: string | null }>
}

export interface IHealthCheckDefinition {
  id: string
  name: string
  group: string
  provider: string
  priority: string
  description: string
  sideEffect: string
  kind: string
  remediation: string
}

export interface AdminHealthState {
  latest: IHealthRun | null
  latestStatus: StatusEnum
  runs: IHealthRunSummary[]
  runsStatus: StatusEnum
  selectedRun: IHealthRun | null
  selectedRunStatus: StatusEnum
  checks: IHealthCheckDefinition[]
  checksStatus: StatusEnum
  triggerStatus: StatusEnum
  activeRunId: string | null
  error: string | null
  /** Set when the backend cannot be reached for reads; cleared on the next success. */
  connectionError: string | null
}

const initialState: AdminHealthState = {
  latest: null,
  latestStatus: StatusEnum.Idle,
  runs: [],
  runsStatus: StatusEnum.Idle,
  selectedRun: null,
  selectedRunStatus: StatusEnum.Idle,
  checks: [],
  checksStatus: StatusEnum.Idle,
  triggerStatus: StatusEnum.Idle,
  activeRunId: null,
  error: null,
  connectionError: null,
}

const BASE = '/internal/test-suite'

function reject(thunkAPI: any, response: APIResponse) {
  if (!response.status) return thunkAPI.rejectWithValue('Network error. Please check your internet.')
  const message = response.data?.message || `Request failed (${response.status})`
  return thunkAPI.rejectWithValue(message)
}

export const getLatestHealthAsync = createAsyncThunk('health/getLatest', async (_: void, thunkAPI) => {
  const response: APIResponse = await ApiGet(`${BASE}/results/latest`)
    .then((res) => res)
    .catch((err) => err)
  if (response.status === 200 && response.data?.code === 'success') {
    return thunkAPI.fulfillWithValue(response.data.data as IHealthRun | { run: null })
  }
  return reject(thunkAPI, response)
})

export const getHealthRunsAsync = createAsyncThunk('health/getRuns', async (_: void, thunkAPI) => {
  const response: APIResponse = await ApiGet(`${BASE}/runs?limit=25`)
    .then((res) => res)
    .catch((err) => err)
  if (response.status === 200 && response.data?.code === 'success') {
    return thunkAPI.fulfillWithValue(response.data.data.items as IHealthRunSummary[])
  }
  return reject(thunkAPI, response)
})

export const getHealthRunAsync = createAsyncThunk('health/getRun', async (runId: string, thunkAPI) => {
  const response: APIResponse = await ApiGet(`${BASE}/runs/${runId}`)
    .then((res) => res)
    .catch((err) => err)
  if (response.status === 200 && response.data?.code === 'success') {
    return thunkAPI.fulfillWithValue(response.data.data as IHealthRun)
  }
  return reject(thunkAPI, response)
})

export const getHealthChecksAsync = createAsyncThunk('health/getChecks', async (_: void, thunkAPI) => {
  const response: APIResponse = await ApiGet(`${BASE}/checks`)
    .then((res) => res)
    .catch((err) => err)
  if (response.status === 200 && response.data?.code === 'success') {
    return thunkAPI.fulfillWithValue(response.data.data as IHealthCheckDefinition[])
  }
  return reject(thunkAPI, response)
})

export const triggerHealthRunAsync = createAsyncThunk(
  'health/trigger',
  async ({ groups, allowSideEffects }: { groups: string[]; allowSideEffects: boolean }, thunkAPI) => {
    const response: APIResponse = await ApiPost(`${BASE}/runs`, {
      groups,
      allowSideEffects,
      triggerSource: 'dashboard',
    })
      .then((res) => res)
      .catch((err) => err)
    if ((response.status === 202 || response.status === 200) && response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data.runId as string)
    }
    return reject(thunkAPI, response)
  },
)

export const healthSlice = createSlice({
  name: 'health',
  initialState,
  reducers: {
    clearSelectedRun: (state) => {
      state.selectedRun = null
      state.selectedRunStatus = StatusEnum.Idle
    },
    setActiveRunId: (state, action: PayloadAction<string | null>) => {
      state.activeRunId = action.payload
    },
    resetHealthError: (state) => {
      state.error = null
      state.triggerStatus = StatusEnum.Idle
    },
  },
  extraReducers(builder) {
    builder.addCase(getLatestHealthAsync.pending, (state) => {
      state.latestStatus = StatusEnum.Pending
    })
    builder.addCase(getLatestHealthAsync.fulfilled, (state, action) => {
      const payload: any = action.payload
      state.latest = payload && payload.runId ? (payload as IHealthRun) : null
      state.latestStatus = StatusEnum.Success
      state.connectionError = null
    })
    builder.addCase(getLatestHealthAsync.rejected, (state, action) => {
      state.latestStatus = StatusEnum.Failed
      // Keep whatever was loaded before; surface as a connection problem, not a toast.
      state.connectionError = (action.payload as string) || 'Failed to load latest results'
    })

    builder.addCase(getHealthRunsAsync.pending, (state) => {
      state.runsStatus = StatusEnum.Pending
    })
    builder.addCase(getHealthRunsAsync.fulfilled, (state, action) => {
      const items = action.payload as IHealthRunSummary[]
      state.runs = items
      state.runsStatus = StatusEnum.Success
      state.connectionError = null
      const running = items.find((r: IHealthRunSummary) => r.status === 'RUNNING' || r.status === 'PENDING')
      state.activeRunId = running ? running.runId : null
    })
    builder.addCase(getHealthRunsAsync.rejected, (state, action) => {
      state.runsStatus = StatusEnum.Failed
      state.connectionError = (action.payload as string) || 'Failed to load run history'
    })

    builder.addCase(getHealthRunAsync.pending, (state) => {
      state.selectedRunStatus = StatusEnum.Pending
    })
    builder.addCase(getHealthRunAsync.fulfilled, (state, action) => {
      state.selectedRun = action.payload as IHealthRun
      state.selectedRunStatus = StatusEnum.Success
    })
    builder.addCase(getHealthRunAsync.rejected, (state, action) => {
      state.selectedRunStatus = StatusEnum.Failed
      state.error = (action.payload as string) || 'Failed to load run'
    })

    builder.addCase(getHealthChecksAsync.pending, (state) => {
      state.checksStatus = StatusEnum.Pending
    })
    builder.addCase(getHealthChecksAsync.fulfilled, (state, action) => {
      state.checks = action.payload as IHealthCheckDefinition[]
      state.checksStatus = StatusEnum.Success
    })
    builder.addCase(getHealthChecksAsync.rejected, (state) => {
      state.checksStatus = StatusEnum.Failed
    })

    builder.addCase(triggerHealthRunAsync.pending, (state) => {
      state.triggerStatus = StatusEnum.Pending
      state.error = null
    })
    builder.addCase(triggerHealthRunAsync.fulfilled, (state, action) => {
      state.triggerStatus = StatusEnum.Success
      state.activeRunId = action.payload as string
    })
    builder.addCase(triggerHealthRunAsync.rejected, (state, action) => {
      state.triggerStatus = StatusEnum.Failed
      state.error = (action.payload as string) || 'Failed to start a run'
    })
  },
})

export const { clearSelectedRun, setActiveRunId, resetHealthError } = healthSlice.actions
export default healthSlice.reducer
